import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider } from './auth/AuthProvider';
import { msalInstance } from './auth/msalConfig';
import router from './routes/router';

/**
 * Module-scope QueryClient with stale-while-revalidate defaults.
 * staleTime: 30 minutes — page navigation feels instant (cached data shown immediately).
 * gcTime: 60 minutes — data kept in memory for an hour after components unmount.
 * retry: 2 — retry failed requests twice before surfacing an error.
 * refetchOnWindowFocus: true — data refreshed when user switches back to the tab.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 60 * 1000,
      gcTime: 60 * 60 * 1000,
      retry: 2,
      refetchOnWindowFocus: true,
    },
  },
});

/**
 * Async bootstrap ensures MSAL is fully ready before React renders.
 *
 * Sequence:
 * 1. initialize() — required before any MSAL API call (v5 requirement)
 * 2. handleRedirectPromise() — processes the Azure AD redirect response hash;
 *    without this, the auth response is never consumed after redirect-based login
 * 3. Render the React tree
 *
 * The .catch() on bootstrap() prevents a silent blank page if MSAL init fails
 * (e.g., corrupted session storage, network issues reaching Azure AD metadata).
 */
async function bootstrap() {
  await msalInstance.initialize();
  try {
    await msalInstance.handleRedirectPromise({ navigateToLoginRequestUrl: false });
  } catch (err) {
    // Clear stale MSAL cache entries that cause no_token_request_cache_error
    // (e.g., sessionStorage leftover from a previous redirect that was interrupted).
    // Only remove MSAL-owned keys to preserve other sessionStorage data.
    // AuthGuard will trigger a fresh loginRedirect on next render.
    console.warn('[bootstrap] handleRedirectPromise failed, clearing MSAL cache', err);
    const msalKeys = Object.keys(sessionStorage).filter(
      (key) => key.startsWith('msal.') || key.includes('login.windows.net')
    );
    for (const key of msalKeys) {
      sessionStorage.removeItem(key);
    }
  }

  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('Root element not found');
  }

  createRoot(rootElement).render(
    <StrictMode>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
      </AuthProvider>
    </StrictMode>
  );
}

bootstrap().catch((err: unknown) => {
  const rootElement = document.getElementById('root');
  if (rootElement) {
    const wrapper = document.createElement('div');
    wrapper.setAttribute('style', 'padding:2rem;font-family:system-ui');

    const heading = document.createElement('h1');
    heading.textContent = 'Failed to initialize';

    const hint = document.createElement('p');
    hint.textContent =
      'Try clearing your browser data for this site or opening in a private window.';

    const pre = document.createElement('pre');
    pre.setAttribute('style', 'background:#f5f5f5;padding:1rem;overflow:auto');
    pre.textContent = String(err);

    wrapper.appendChild(heading);
    wrapper.appendChild(hint);
    wrapper.appendChild(pre);
    rootElement.appendChild(wrapper);
  }
});
